import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatTableDataSource, MatDialog, MatSnackBar } from '@angular/material';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { ComponentPageTitle } from 'src/app/page-title/page-title.component';
import { GatewayService } from 'src/app/gateway.service';
import { LocalSaveService } from 'src/app/services/local-save-service.service';
import { BusquedaService } from '../busqueda.service';
import { CreateService } from '../createservice';
import { EscenarioAreaFormComponent } from './escenario-area-form/escenario-area-form.component';
import { AlarmasPermisosService } from 'src/app/services/alarmas-permisos.service';

/**
 * Pantalla admin: mapeo escenarios <-> areas de agente para alarmas infructuosas.
 * Reemplaza el catalogo hardcoded que vivia en alarmas_tecnico.queries.js
 * y en home-agente.component.ts (validAgenteParaAlarmas).
 *
 * Visible solo para superadmin (ver menu en home.component.html).
 */
@Component({
  selector: 'app-escenario-area',
  templateUrl: './escenario-area.component.html',
  styleUrls: ['./escenario-area.component.scss']
})
export class EscenarioAreaComponent implements OnInit {
  private destroy$ = new Subject<void>();

  columns: string[] = ['ESCENARIO', 'TIPO', 'AREA', 'VER', 'GESTIONAR', 'ESTADO', 'ACCION'];
  dataSource = new MatTableDataSource<any>([]);
  private subscriptionCreate: Subscription;
  private subscriptionBusqueda: Subscription;

  filter: string = '';
  resultsLength = 0;
  areas: any[] = [];
  escenarios: any[] = [];

  constructor(
    private router: Router,
    private spinner: NgxSpinnerService,
    private gateway: GatewayService,
    public pageTitle: ComponentPageTitle,
    public _busqueda: BusquedaService,
    public _create: CreateService,
    private dialog: MatDialog,
    private snackbar: MatSnackBar,
    private localSave: LocalSaveService,
    private alarmasPermisos: AlarmasPermisosService,
  ) { }

  ngOnInit() {
    this.pageTitle.title = 'Escenarios de Alarmas por Área';

    const user = this.localSave.get('usuarioPGV');
    if (!user || Number(user.idrol) !== 10) {
      this.router.navigate(['/home']);
      return;
    }

    this.cargarAreas();
    this.cargarEscenarios();
    this.cargar();

    this.subscriptionCreate = this._create.notifyObservable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.abrirFormulario());

    this.subscriptionBusqueda = this._busqueda.notifyObservable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => this.filtrar(res.filtro));
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.subscriptionCreate) this.subscriptionCreate.unsubscribe();
    if (this.subscriptionBusqueda) this.subscriptionBusqueda.unsubscribe();
  }

  cargarAreas() {
    this.gateway.get('escenarioAreaListarAreas', {}).then((res: any) => {
      this.areas = Array.isArray(res.rows) ? res.rows : [];
    }).catch(() => {
      this.areas = [];
    });
  }

  cargarEscenarios() {
    this.gateway.get('escenarioAreaListarEscenarios', {}).then((res: any) => {
      this.escenarios = Array.isArray(res.rows) ? res.rows : [];
    }).catch(() => {
      this.escenarios = [];
    });
  }

  cargar() {
    this.spinner.show();
    this.gateway.get('escenarioAreaListar', {}).then((res: any) => {
      const rows = Array.isArray(res.rows) ? res.rows : [];
      this.dataSource = new MatTableDataSource<any>(rows);
      this.resultsLength = rows.length;
      this.spinner.hide();
    }).catch(() => {
      this.spinner.hide();
    });
  }

  filtrar(texto: string) {
    this.dataSource.filter = (texto || '').trim().toLowerCase();
    this.resultsLength = this.dataSource.filteredData.length;
  }

  keyDownFunction(event) {
    if (event.keyCode === 13 || (event.keyCode === 8 && this.filter === '')) {
      this._busqueda.notificarBusqueda({ filtro: this.filter });
    }
  }

  filtrogeneral() {
    this._busqueda.notificarBusqueda({ filtro: this.filter });
  }

  abrirFormulario(element?: any) {
    const dialogRef = this.dialog.open(EscenarioAreaFormComponent, {
      maxWidth: '90vw',
      width: '520px',
      minHeight: '40vh',
      disableClose: true,
      data: {
        operacion: element ? 'Editar' : 'Crear',
        registro: element ? Object.assign({}, element) : null,
        areas: this.areas,
        escenarios: this.escenarios
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Invalidar cache de permisos en memoria del agente actual (admin)
        this.alarmasPermisos.invalidar();
        this.cargar();
      }
    });
  }

  toggleEstado(element) {
    const payload = {
      id: element.id,
      idescenario: element.idescenario,
      idareaagente: element.idareaagente,
      puede_ver_alarmas: element.puede_ver_alarmas,
      puede_gestionar: element.puede_gestionar,
      estado: element.estado === 1 ? 0 : 1
    };
    this.gateway.get('escenarioAreaActualizar', payload).then((res: any) => {
      if (res && res.response === 1) {
        this.snackbar.open(res.message || 'Estado actualizado', 'Cerrar', { duration: 3000 });
        this.alarmasPermisos.invalidar();
        this.cargar();
      } else {
        this.snackbar.open((res && res.message) || 'Error al actualizar', 'Cerrar', { duration: 4000 });
      }
    });
  }
}
